import React, { useEffect, useRef, useState } from 'react';

interface MenuBarProps {
    t: any;
    showMenuDropdown: string | null;
    showRuler: boolean;
    printLayout: boolean;
    isFullscreen: boolean;
    lineSpacing: number;
    keepWithNext: boolean;
    keepLinesTogether: boolean;
    preventSingleLines: boolean;
    pageBreakBefore: boolean;
    onToggleMenu: (menu: string) => void;
    onMenuAction: (action: string) => void;
    onSetShowRuler: (show: boolean) => void;
    onSetShowMenuDropdown: (menu: string | null) => void;
    onSetShowCustomSpacingModal: (show: boolean) => void;
    onHandleSpacingChange: (spacing: number) => void;
    onHandleAddSpaceBefore: () => void;
    onHandleRemoveSpaceBefore: () => void;
    onHandleAddPageBreak: () => void;
    onSetKeepWithNext: (value: boolean) => void;
    onSetKeepLinesTogether: (value: boolean) => void;
    onSetPreventSingleLines: (value: boolean) => void;
    onClearFormatting: () => void;
    /** Importing makes documents of your own; not offered inside someone
        else's. Read-only drops every menu that changes the words. */
    canImport?: boolean;
    readOnly?: boolean;
}

// Only one submenu is open at a time. Hovering a different trigger — or any
// plain option in the same dropdown — closes the previous panel on the spot;
// the grace timer only covers the diagonal trip from trigger to its own panel,
// so panels never overlap each other.
const SubmenuContext = React.createContext<{
    openLabel: string | null;
    setOpenLabel: React.Dispatch<React.SetStateAction<string | null>>;
}>({ openLabel: null, setOpenLabel: () => {} });

const MenuDropdown: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [openLabel, setOpenLabel] = useState<string | null>(null);

    return (
        <SubmenuContext.Provider value={{ openLabel, setOpenLabel }}>
            <div
                className="menu-dropdown"
                // mouseover bubbles, so this catches the plain options too
                onMouseOver={(e) => {
                    if (!(e.target as HTMLElement).closest('.menu-option-with-submenu')) {
                        setOpenLabel(null);
                    }
                }}
            >
                {children}
            </div>
        </SubmenuContext.Provider>
    );
};

const HoverSubmenu: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
    const { openLabel, setOpenLabel } = React.useContext(SubmenuContext);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const open = openLabel === label;

    const cancelClose = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    useEffect(() => cancelClose, []);

    return (
        <div
            className="menu-option-with-submenu"
            onMouseEnter={() => { cancelClose(); setOpenLabel(label); }}
            onMouseLeave={() => {
                timeoutRef.current = setTimeout(() => {
                    // Another submenu may have opened in the meantime — leave it be
                    setOpenLabel((current) => (current === label ? null : current));
                }, 250);
            }}
        >
            <button className="menu-option submenu-trigger">{label} ▸</button>
            {open && (
                <div
                    className="menu-submenu"
                    onMouseEnter={cancelClose}
                    onMouseLeave={() => setOpenLabel((current) => (current === label ? null : current))}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export const MenuBar: React.FC<MenuBarProps> = ({
    t,
    showMenuDropdown,
    showRuler,
    printLayout,
    isFullscreen,
    lineSpacing,
    keepWithNext,
    keepLinesTogether,
    preventSingleLines,
    pageBreakBefore,
    onToggleMenu,
    onMenuAction,
    onSetShowRuler,
    onSetShowMenuDropdown,
    onSetShowCustomSpacingModal,
    onHandleSpacingChange,
    onHandleAddSpaceBefore,
    onHandleRemoveSpaceBefore,
    onHandleAddPageBreak,
    onSetKeepWithNext,
    onSetKeepLinesTogether,
    onSetPreventSingleLines,
    onClearFormatting,
    canImport = true,
    readOnly = false,
}) => {
    return (
        <div className="doc-menubar">
            <div className="menu-item">
                <button className="menu-label" onClick={() => onToggleMenu('file')}>
                    {t.editor.file}
                </button>
                {showMenuDropdown === 'file' && (
                    <MenuDropdown>
                        {canImport && (
                            <>
                                <HoverSubmenu label={t.editor.importMenu}>
                                    <button className="menu-option" onClick={() => onMenuAction('importGoogleDocs')}>
                                        📄 Google Docs
                                    </button>
                                    <button className="menu-option" onClick={() => onMenuAction('importLocalFiles')}>
                                        💻 {t.editor.importLocalComputer}
                                    </button>
                                </HoverSubmenu>
                                <div className="menu-divider"></div>
                            </>
                        )}
                        <button className="menu-option" onClick={() => onMenuAction('versionHistory')}>
                            {t.editor.versionHistory}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('print')}>
                            {t.editor.print}
                        </button>
                        <div className="menu-divider"></div>
                        <HoverSubmenu label={t.editor.download}>
                            <button className="menu-option" onClick={() => onMenuAction('downloadPdf')}>
                                {t.editor.downloadPdf}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('downloadDocx')}>
                                {t.editor.downloadWord}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('downloadRtf')}>
                                {t.editor.downloadRichText}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('downloadHtml')}>
                                {t.editor.downloadWebPage}
                            </button>
                        </HoverSubmenu>
                    </MenuDropdown>
                )}
            </div>

            {!readOnly && <div className="menu-item">
                <button className="menu-label" onClick={() => onToggleMenu('edit')}>
                    {t.editor.edit}
                </button>
                {showMenuDropdown === 'edit' && (
                    <MenuDropdown>
                        <button className="menu-option" onClick={() => onMenuAction('undo')}>
                            <span className="menu-shortcut">⌘Z</span>
                            {t.editor.undo}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('redo')}>
                            <span className="menu-shortcut">⌘⇧Z</span>
                            {t.editor.redo}
                        </button>
                        <div className="menu-divider"></div>
                        <button className="menu-option" onClick={() => onMenuAction('cut')}>
                            <span className="menu-shortcut">⌘X</span>
                            {t.editor.cut}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('copy')}>
                            <span className="menu-shortcut">⌘C</span>
                            {t.editor.copy}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('paste')}>
                            <span className="menu-shortcut">⌘V</span>
                            {t.editor.paste}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('pastePlain')}>
                            <span className="menu-shortcut">⌘⇧V</span>
                            {t.editor.paste} <span className="menu-label-note">{t.editor.withoutFormatting}</span>
                        </button>
                        <div className="menu-divider"></div>
                        <button className="menu-option" onClick={() => onMenuAction('selectAll')}>
                            <span className="menu-shortcut">⌘A</span>
                            {t.editor.selectAll}
                        </button>
                    </MenuDropdown>
                )}
            </div>}

            <div className="menu-item">
                <button className="menu-label" onClick={() => onToggleMenu('view')}>
                    {t.editor.view}
                </button>
                {showMenuDropdown === 'view' && (
                    <MenuDropdown>
                        <button className="menu-option" onClick={() => {
                            onSetShowRuler(!showRuler);
                            onSetShowMenuDropdown(null);
                        }}>
                            {showRuler && <span className="menu-checkmark">✓</span>}
                            {t.editor.showRuler}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('printLayout')}>
                            {printLayout && <span className="menu-checkmark">✓</span>}
                            {t.editor.printLayout}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('fullScreen')}>
                            {isFullscreen && <span className="menu-checkmark">✓</span>}
                            {t.editor.fullScreen}
                        </button>
                    </MenuDropdown>
                )}
            </div>

            {!readOnly && <div className="menu-item">
                <button className="menu-label" onClick={() => onToggleMenu('insert')}>
                    {t.editor.insert}
                </button>
                {showMenuDropdown === 'insert' && (
                    <MenuDropdown>
                        <button className="menu-option" onClick={() => onMenuAction('insertImage')}>
                            🖼️ {t.editor.image}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('insertTable')}>
                            ⊞ {t.editor.table}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('insertLink')}>
                            <span className="menu-shortcut">⌘K</span>
                            🔗 {t.editor.link}
                        </button>
                        <div className="menu-divider"></div>
                        <button className="menu-option" onClick={() => onMenuAction('insertHorizontalRule')}>
                            — {t.editor.horizontalRule}
                        </button>
                    </MenuDropdown>
                )}
            </div>}

            {!readOnly && <div className="menu-item">
                <button className="menu-label" onClick={() => onToggleMenu('format')}>
                    {t.editor.format}
                </button>
                {showMenuDropdown === 'format' && (
                    <MenuDropdown>
                        <HoverSubmenu label={t.editor.text}>
                            <button className="menu-option" onClick={() => onMenuAction('bold')}>
                                <span className="menu-shortcut">⌘B</span>
                                {t.editor.bold}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('italic')}>
                                <span className="menu-shortcut">⌘I</span>
                                {t.editor.italic}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('underline')}>
                                <span className="menu-shortcut">⌘U</span>
                                {t.editor.underline}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('strikethrough')}>
                                {t.editor.strikethrough}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('superscript')}>
                                {t.editor.superscript}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('subscript')}>
                                {t.editor.subscript}
                            </button>
                            <div className="menu-divider"></div>
                            <button className="menu-option" onClick={() => onMenuAction('upperCase')}>
                                {t.editor.upperCase}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('lowerCase')}>
                                {t.editor.lowerCase}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('titleCase')}>
                                {t.editor.titleCase}
                            </button>
                        </HoverSubmenu>
                        <HoverSubmenu label={t.editor.alignAndIndent}>
                            <button className="menu-option" onClick={() => onMenuAction('alignLeft')}>
                                <span className="menu-shortcut">⌘⇧L</span>
                                {t.editor.left}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('alignCenter')}>
                                <span className="menu-shortcut">⌘⇧E</span>
                                {t.editor.center}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('alignRight')}>
                                <span className="menu-shortcut">⌘⇧R</span>
                                {t.editor.right}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('alignJustify')}>
                                <span className="menu-shortcut">⌘⇧J</span>
                                {t.editor.justify}
                            </button>
                            <div className="menu-divider"></div>
                            <button className="menu-option" onClick={() => onMenuAction('indent')}>
                                {t.editor.increaseIndent}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('outdent')}>
                                {t.editor.decreaseIndent}
                            </button>
                        </HoverSubmenu>
                        <HoverSubmenu label={t.editor.script}>
                            <button className="menu-option" onClick={() => onMenuAction('script:scene')}>
                                {t.editor.scriptScene} <span className="menu-label-note">(INT./EXT.)</span>
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('script:action')}>
                                {t.editor.scriptAction}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('script:character')}>
                                {t.editor.scriptCharacter}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('script:dialogue')}>
                                {t.editor.scriptDialogue}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('script:parenthetical')}>
                                {t.editor.scriptParenthetical}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('script:transition')}>
                                {t.editor.scriptTransition} <span className="menu-label-note">(CUT TO:)</span>
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('script:none')}>
                                {t.editor.scriptNone}
                            </button>
                            <div className="menu-separator"></div>
                            <button className="menu-option" disabled style={{ opacity: 0.7, cursor: 'default' }}>
                                {t.editor.scriptHint} <span className="menu-label-note">{t.editor.scriptHintNote}</span>
                            </button>
                        </HoverSubmenu>
                        <HoverSubmenu label={t.editor.bulletsAndNumbering}>
                            <button className="menu-option" onClick={() => onMenuAction('bulletList')}>
                                <span className="menu-shortcut">⌘⇧8</span>
                                • {t.editor.bulletList}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('orderedList')}>
                                <span className="menu-shortcut">⌘⇧7</span>
                                1. {t.editor.numberedList}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('taskList')}>
                                ☑ {t.editor.checkList}
                            </button>
                        </HoverSubmenu>
                        <HoverSubmenu label={t.editor.lineAndSpacing}>
                            <button className="menu-option" onClick={() => onHandleSpacingChange(1.0)}>
                                {lineSpacing === 1.0 && <span className="menu-checkmark">✓</span>}
                                {t.editor.spacingSingle}
                            </button>
                            <button className="menu-option" onClick={() => onHandleSpacingChange(1.15)}>
                                {lineSpacing === 1.15 && <span className="menu-checkmark">✓</span>}
                                1.15
                            </button>
                            <button className="menu-option" onClick={() => onHandleSpacingChange(1.5)}>
                                {lineSpacing === 1.5 && <span className="menu-checkmark">✓</span>}
                                1.5
                            </button>
                            <button className="menu-option" onClick={() => onHandleSpacingChange(2.0)}>
                                {lineSpacing === 2.0 && <span className="menu-checkmark">✓</span>}
                                {t.editor.spacingDouble}
                            </button>
                            <div className="menu-divider"></div>
                            <button className="menu-option" onClick={onHandleAddSpaceBefore}>
                                {t.editor.addSpaceBefore}
                            </button>
                            <button className="menu-option" onClick={onHandleRemoveSpaceBefore}>
                                {t.editor.removeSpaceBefore}
                            </button>
                            <div className="menu-divider"></div>
                            <button className="menu-option" onClick={() => {
                                onSetShowCustomSpacingModal(true);
                                onSetShowMenuDropdown(null);
                            }}>
                                {t.editor.customSpacingMenu}
                            </button>
                            <div className="menu-divider"></div>
                            <button className="menu-option" onClick={() => onSetKeepWithNext(!keepWithNext)}>
                                {keepWithNext && <span className="menu-checkmark">✓</span>}
                                {t.editor.keepWithNext}
                            </button>
                            <button className="menu-option" onClick={() => onSetKeepLinesTogether(!keepLinesTogether)}>
                                {keepLinesTogether && <span className="menu-checkmark">✓</span>}
                                {t.editor.keepLinesTogether}
                            </button>
                            <button className="menu-option" onClick={() => onSetPreventSingleLines(!preventSingleLines)}>
                                {preventSingleLines && <span className="menu-checkmark">✓</span>}
                                {t.editor.preventSingleLines}
                            </button>
                            <button className="menu-option" onClick={onHandleAddPageBreak}>
                                {pageBreakBefore && <span className="menu-checkmark">✓</span>}
                                {t.editor.addPageBreakBefore}
                            </button>
                        </HoverSubmenu>
                        <HoverSubmenu label={t.editor.table}>
                            <button className="menu-option" onClick={() => onMenuAction('tableAddRowAbove')}>
                                {t.editor.insertRowAbove}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('tableAddRowBelow')}>
                                {t.editor.insertRowBelow}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('tableAddColumnLeft')}>
                                {t.editor.insertColumnLeft}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('tableAddColumnRight')}>
                                {t.editor.insertColumnRight}
                            </button>
                            <div className="menu-divider"></div>
                            <button className="menu-option" onClick={() => onMenuAction('tableToggleHeaderRow')}>
                                {t.editor.toggleHeaderRow}
                            </button>
                            <div className="menu-divider"></div>
                            <button className="menu-option" onClick={() => onMenuAction('tableDeleteRow')}>
                                {t.editor.deleteRow}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('tableDeleteColumn')}>
                                {t.editor.deleteColumn}
                            </button>
                            <button className="menu-option" onClick={() => onMenuAction('tableDelete')}>
                                {t.editor.deleteTable}
                            </button>
                        </HoverSubmenu>
                        <div className="menu-divider"></div>
                        <button className="menu-option" onClick={onClearFormatting}>
                            {t.editor.clearFormatting}
                        </button>
                    </MenuDropdown>
                )}
            </div>}

            {!readOnly && <div className="menu-item">
                <button className="menu-label" onClick={() => onToggleMenu('tools')}>
                    {t.editor.tools}
                </button>
                {showMenuDropdown === 'tools' && (
                    <MenuDropdown>
                        <button className="menu-option" onClick={() => onMenuAction('wordCount')}>
                            {t.editor.wordCount}
                        </button>
                        <button className="menu-option" onClick={() => onMenuAction('proofreadSettings')}>
                            {t.editor.proofreadSettings}
                        </button>
                    </MenuDropdown>
                )}
            </div>}
        </div>
    );
};
